/**
 * Warwick Design System - Effects Tokens
 * Shadows, transitions, border radius, and other visual effects
 * TV-optimized for performance
 */

// Border radius scale
export const borderRadius = {
  none: '0',
  sm: '0.25rem', // 4px
  md: '0.5rem', // 8px
  lg: '0.75rem', // 12px
  xl: '1rem', // 16px
  '2xl': '1.25rem', // 20px
  '3xl': '1.5rem', // 24px
  full: '9999px', // Fully rounded

  // Component-specific radius
  card: '1.75rem', // 28px - Current card radius from variables
  nav: '4.164375rem', // 66.63px - Bottom nav radius
};

// Transitions (TV-optimized for performance)
export const transitions = {
  // Duration
  duration: {
    fast: '0.15s',
    normal: '0.2s',
    slow: '0.25s',
    slower: '0.35s',
  },

  // Easing
  easing: {
    easeOut: 'ease-out',
    easeIn: 'ease-in',
    easeInOut: 'ease-in-out',
    linear: 'linear',
  },

  // Common transition patterns
  default: 'all 0.2s ease-out',
  transform: 'transform 0.2s ease-out',
  opacity: 'opacity 0.2s ease-out',
  background: 'background 0.15s ease-out',

  // Component-specific transitions
  serviceCard: {
    transform: 'transform 0.2s ease-out',
    image: 'opacity 0.2s ease-out, filter 0.2s ease-out',
  },

  navButton: {
    combined: 'transform 0.15s ease-out, background 0.15s ease-out',
  },
};

// Shadows (minimal for TV performance)
export const shadows = {
  none: 'none',
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px rgba(0, 0, 0, 0.1)',
  lg: '0 8px 15px rgba(0, 0, 0, 0.1)',

  // TV-optimized: avoid heavy shadows
  card: 'none', // Cards use borders instead
  focus: '0 0 0 3px rgba(196, 170, 111, 0.4)', // Gold focus ring
};

// Transforms (TV performance optimized)
export const transforms = {
  // Scale transforms
  scale: {
    sm: 'scale(0.95)',
    normal: 'scale(1)',
    lg: 'scale(1.05)',
    xl: 'scale(1.1)',
  },

  // Component-specific transforms
  serviceCard: {
    active: 'scale(1.1)',
    inactive: 'scale(1)',
  },

  navButton: {
    hover: 'scale(1.1)',
    normal: 'scale(1)',
  },
};

// Opacity levels
export const opacity = {
  invisible: '0',
  low: '0.3',
  medium: '0.5',
  high: '0.8',
  visible: '1',

  // Component-specific opacity
  serviceCard: {
    inactive: '0.5',
    active: '1',
  },

  overlay: {
    light: '0.2',
    medium: '0.3',
    heavy: '0.7',
    dark: '0.8',
  },
};

// Filters (TV-optimized)
export const filters = {
  none: 'none',
  blur: {
    sm: 'blur(2px)',
    md: 'blur(4px)', // Used in service cards
    lg: 'blur(8px)',
  },

  // Component-specific filters
  serviceCard: {
    active: 'none',
    inactive: 'blur(4px)',
  },
};

// Animation keyframes (if needed)
export const animations = {
  // Subtle fade in for TV performance
  fadeIn: {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },

  // Slide animations
  slideInLeft: {
    from: { transform: 'translateX(-100%)' },
    to: { transform: 'translateX(0)' },
  },

  slideInRight: {
    from: { transform: 'translateX(100%)' },
    to: { transform: 'translateX(0)' },
  },
};

// Performance optimizations
export const performance = {
  // Contain properties for better performance
  contain: {
    layout: 'layout',
    paint: 'paint',
    strict: 'strict',
  },
};
